/**
 * WI-workos-07 — verify WorkOS OIDC claims and map them to Mark current-user.
 *
 * DS-workos-03 is resolved only at the existing ProjectContext identity seam;
 * no middleware framework is selected. PIN-workos-B is explicit: the human
 * token source is WorkOS's published issuer/JWKS surface, never the deferred
 * cluster workload-issuer discovery bucket.
 */

import {
  WORKOS_FIXTURE_JWKS_KEY,
  fixtureSign,
  type WorkosClaims,
  type WorkosRole,
} from "./workos-mock";
import { FIXTURE_AUTHKIT_CONFIG, WORKOS_ISSUER } from "./workos-config";

export interface WorkosOidcSource {
  issuer: typeof WORKOS_ISSUER;
  jwks_uri: string;
  plane: "workos-human";
}

/** Bounded clock-skew allowance (seconds) for the `iat` not-before check. A real
 * OIDC verifier tolerates a few seconds of drift between the issuer's clock and
 * ours; this is that bound and nothing more. A token issued further into the
 * future than this is rejected as not-yet-valid. Kept small (standard OIDC
 * leeway) — large enough to absorb genuine clock drift, small enough that a
 * future-dated forgery gains nothing. */
export const IAT_SKEW_TOLERANCE_SECONDS = 5;

/** WorkOS-published human-plane JWKS endpoint, pinned to the AuthKit client.
 * The fixture key stands in for the key returned there; no network/live leg is
 * attempted. This is intentionally unrelated to any `oidc.<apex>` resource. */
export const WORKOS_HUMAN_OIDC_SOURCE: WorkosOidcSource = {
  issuer: WORKOS_ISSUER,
  jwks_uri: `https://api.workos.com/sso/jwks/${FIXTURE_AUTHKIT_CONFIG.client_id}`,
  plane: "workos-human",
};

export interface WorkosCurrentUser {
  user_id: string;
  org_id: string;
  role: WorkosRole;
}

function decodeBase64Url(part: string): string {
  const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  return atob(base64 + padding);
}

function isWorkosRole(value: unknown): value is WorkosRole {
  return value === "member" || value === "supervisor" || value === "admin";
}

/** Verify a WorkOS fixture token against the pinned WorkOS human-plane source,
 * then map `{sub, org_id, role}` onto Mark's current-user shape. `nowSeconds` is
 * the verification clock (injectable for tests); a token whose `exp` is at/before
 * it is REJECTED, so a captured/expired token can never establish a session. */
export function verifyAndMapWorkosToken(
  token: string,
  source: WorkosOidcSource = WORKOS_HUMAN_OIDC_SOURCE,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): WorkosCurrentUser {
  if (
    source.issuer !== WORKOS_ISSUER ||
    source.plane !== "workos-human" ||
    !source.jwks_uri.startsWith("https://api.workos.com/")
  ) {
    throw new Error("untrusted WorkOS issuer/JWKS source");
  }

  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("invalid WorkOS token shape");
  const [encodedHeader, encodedPayload, signature] = parts;

  let header: { alg?: unknown; typ?: unknown; kid?: unknown };
  let claims: Partial<WorkosClaims>;
  try {
    header = JSON.parse(decodeBase64Url(encodedHeader)) as typeof header;
    claims = JSON.parse(decodeBase64Url(encodedPayload)) as Partial<WorkosClaims>;
  } catch {
    throw new Error("invalid WorkOS token encoding");
  }

  if (header.alg !== "HS256" || header.typ !== "JWT" || header.kid !== "workos-fixture") {
    throw new Error("untrusted WorkOS fixture key metadata");
  }
  const expected = fixtureSign(
    `${encodedHeader}.${encodedPayload}`,
    WORKOS_FIXTURE_JWKS_KEY,
  );
  if (signature !== expected) throw new Error("invalid WorkOS token signature");
  if (
    claims.iss !== source.issuer ||
    typeof claims.sub !== "string" ||
    claims.sub.length === 0 ||
    typeof claims.org_id !== "string" ||
    claims.org_id.length === 0 ||
    !isWorkosRole(claims.role) ||
    typeof claims.iat !== "number" ||
    typeof claims.exp !== "number"
  ) {
    throw new Error("invalid WorkOS identity claims");
  }
  // Lifetime: reject a token that has expired (or is not yet valid). A captured
  // or replayed AuthKit token past its `exp` must never establish a session —
  // the human re-auths through the hosted flow (→ WI-B-05 auth-stale on the
  // send path, → A1 re-auth on the read path).
  // exp is mandatory (see claim validation above) and a token at/after its
  // expiry never establishes a session.
  if (claims.exp <= nowSeconds) {
    throw new Error("expired WorkOS token");
  }
  // iat must not be in the future beyond a small, bounded clock-skew allowance.
  // Real OIDC verifiers permit a few seconds of drift between the issuer's clock
  // and the verifier's; IAT_SKEW_TOLERANCE_SECONDS is that bound and nothing
  // more — a token issued further ahead than this is rejected as not-yet-valid.
  if (claims.iat > nowSeconds + IAT_SKEW_TOLERANCE_SECONDS) {
    throw new Error("WorkOS token issued in the future");
  }

  return {
    user_id: claims.sub,
    org_id: claims.org_id,
    role: claims.role,
  };
}
